"use client";

import { useMemo, useState } from "react";
import { CheckCircle2, Plus, Trash2, XCircle } from "lucide-react";

import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { t } from "@/lib/localization";
import { CompleteSentenceBlock as CompleteSentenceBlockType } from "@/lib/utils/lomdaV3/types";
import { useLomdaV3Editor } from "../../../store/useLomdaV3Editor";
import { useLomdaV3Theme } from "../../../hooks/useLomdaV3Theme";
import { usePortalContainer } from "../../../hooks/usePortalContainer";
import { useBlockEditable } from "../../../hooks/useBlockEditable";
import { PreviewButton } from "../shared/PreviewButton";
import { stripHtml } from "../../../lib/titleHtml";
import { useLomdaDir, useLomdaLocale } from "../../../hooks/useLomdaLocale";

type BlockTag = "h1" | "h2" | "h3" | "h4" | "h5" | "h6" | "p";

/** A blank is authored inline as `_word_` — the wrapped word is its answer. */
const BLANK_MARK = /_([^_]+)_/g;

/** Loose match: case-insensitive, trims, drops commas/spaces. */
const normalize = (s: string) => s.toLowerCase().trim().replace(/[\s,]/g, "");

/**
 * The sentence defaults to a PARAGRAPH — it's body copy, not a heading, so it
 * needn't be bold/large. A tag the author picked via the toolbar is respected.
 */
const seedSentenceHtml = (html: string): string => {
    const trimmed = (html ?? "").trim();
    if (!trimmed) return "<p></p>";
    if (/^<(h[1-6]|p|ul|ol|blockquote|div|table)/i.test(trimmed)) return trimmed;
    return `<p>${trimmed}</p>`;
};

/** Re-wrap the plain sentence from the textarea back into its current tag, so an
 *  existing H5 sentence keeps its tag while the author edits plain text. */
const wrapSentence = (plain: string, tag: BlockTag): string =>
    plain.trim() ? `<${tag}>${plain}</${tag}>` : "";

/** Pull the wrapping tag + plain text out of the sentence's rich-text HTML, so
 *  the blanks render inline while the tag still drives the size. */
function parseSentence(html: string): { Tag: BlockTag; text: string } {
    const seeded = seedSentenceHtml(html);
    const m = seeded.match(/^<(h[1-6]|p)\b[^>]*>([\s\S]*?)<\/\1>\s*$/i);
    return {
        Tag: (m ? m[1].toLowerCase() : "p") as BlockTag,
        text: (m ? m[2] : seeded).replace(/<[^>]*>/g, ""),
    };
}

/**
 * Width that fits the blank's LONGEST option (and its placeholder), so choosing
 * an answer never resizes the blank and reflows the sentence — the CTO's "no
 * layout shift". `ch` ≈ one character of the inherited font; the addend is the
 * horizontal padding.
 */
const triggerWidth = (opts: string[], placeholder: string): string =>
    `calc(${[...opts, placeholder].reduce(
        (n, o) => Math.max(n, o.length),
        4
    )}ch + 1.5rem)`;

type Segment =
    | { kind: "text"; value: string }
    | { kind: "blank"; answer: string; index: number };

function toSegments(sentence: string): Segment[] {
    const segs: Segment[] = [];
    let last = 0;
    let idx = 0;
    let m: RegExpExecArray | null;
    BLANK_MARK.lastIndex = 0;
    while ((m = BLANK_MARK.exec(sentence))) {
        if (m.index > last)
            segs.push({ kind: "text", value: sentence.slice(last, m.index) });
        segs.push({ kind: "blank", answer: m[1].trim(), index: idx++ });
        last = m.index + m[0].length;
    }
    if (last < sentence.length)
        segs.push({ kind: "text", value: sentence.slice(last) });
    return segs;
}

const isBlank = (s: Segment): s is Extract<Segment, { kind: "blank" }> =>
    s.kind === "blank";

type Blank = Extract<Segment, { kind: "blank" }>;

/** Blanks (answer + index) parsed from the sentence's rich-text HTML. */
const blanksOf = (text: string): Blank[] =>
    toSegments(parseSentence(text).text).filter(isBlank);

function shuffle<T>(arr: T[]): T[] {
    const a = [...arr];
    for (let i = a.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
}

// A page-coloured field inside the card, so its text follows "Text".
const FIELD =
    "lomda-v3-page-text rounded-lg border border-[hsl(var(--lomda-border))] bg-[hsl(var(--lomda-background))] px-3 py-2 lomda-v3-text-p outline-none placeholder:text-[hsl(var(--lomda-muted-text))] focus:border-[hsl(var(--lomda-border-active))]";

/** Edit view — the sentence editor + one option list PER blank (each option is
 *  its own input, with an "add option" button — same UX as the quiz/poll ones). */
function EditMode({
    text,
    blanks,
    answers,
    explanation,
    onText,
    onAnswers,
    onExplanation,
}: {
    text: string;
    blanks: Blank[];
    answers: string[][];
    explanation: string;
    onText: (html: string) => void;
    onAnswers: (answers: string[][]) => void;
    onExplanation: (value: string) => void;
}) {
    // The textarea edits PLAIN text; the tag is preserved across the round-trip.
    const { Tag: sentenceTag, text: sentencePlain } = parseSentence(text);
    // Always emit a dense list aligned to the current blanks, then change one.
    const setBlankOptions = (blankPos: number, opts: string[]) =>
        onAnswers(
            blanks.map((_, i) => (i === blankPos ? opts : answers[i] ?? []))
        );
    const updateOption = (bp: number, oi: number, v: string) => {
        const opts = [...(answers[bp] ?? [])];
        opts[oi] = v;
        setBlankOptions(bp, opts);
    };
    const addOption = (bp: number) =>
        setBlankOptions(bp, [...(answers[bp] ?? []), ""]);
    const removeOption = (bp: number, oi: number) =>
        setBlankOptions(
            bp,
            (answers[bp] ?? []).filter((_, i) => i !== oi)
        );

    return (
        <div className="lomda-v3-card-surface flex flex-col gap-4 rounded-[20px] border border-[hsl(var(--lomda-border))] p-6">
            <div className="flex flex-col gap-1.5">
                <span className="lomda-v3-chrome text-sm font-semibold text-foreground">
                    {t("lomda-v3-editor.complete-sentence.sentence-label")}
                </span>
                {/* A real textarea (not the rich-text editor) so it's obviously
                    an input. Plain text in, re-wrapped into the sentence's tag on
                    the way out — which also keeps `_word_` underscores literal. */}
                <textarea
                    value={sentencePlain}
                    onChange={(e) =>
                        onText(wrapSentence(e.target.value, sentenceTag))
                    }
                    rows={3}
                    placeholder={t(
                        "lomda-v3-editor.complete-sentence.text-placeholder"
                    )}
                    className={cn("w-full resize-y", FIELD)}
                />
                <span className="lomda-v3-chrome text-sm font-semibold text-[hsl(var(--lomda-muted-text))]">
                    {t("lomda-v3-editor.complete-sentence.blank-hint")}
                </span>
            </div>

            {/* One option list per blank — each option its own input + Add button. */}
            {blanks.map((b, bi) => (
                <div key={b.index} className="flex flex-col gap-2">
                    <span className="lomda-v3-chrome text-sm font-semibold text-foreground">
                        {t(
                            "lomda-v3-editor.complete-sentence.distractors-label"
                        )}
                        {" — "}
                        <span className="text-[hsl(var(--lomda-primary))]">
                            {b.answer}
                        </span>
                    </span>
                    {(answers[bi] ?? []).map((opt, oi) => (
                        <div key={oi} className="flex items-center gap-2">
                            <input
                                value={opt}
                                onChange={(e) =>
                                    updateOption(bi, oi, e.target.value)
                                }
                                placeholder={t(
                                    "lomda-v3-editor.quiz.option-placeholder"
                                )}
                                className={cn("min-w-0 flex-1", FIELD)}
                            />
                            <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                onClick={() => removeOption(bi, oi)}
                                className="size-8 shrink-0 text-muted-foreground hover:text-destructive"
                            >
                                <Trash2 className="size-4" />
                            </Button>
                        </div>
                    ))}
                    <Button
                        type="button"
                        variant="outline"
                        onClick={() => addOption(bi)}
                        className="lomda-v3-chrome h-10 gap-2 self-start rounded-lg bg-transparent px-3 text-base font-normal hover:bg-muted"
                    >
                        <Plus className="size-5" />
                        {t("lomda-v3-editor.quiz.add-option")}
                    </Button>
                </div>
            ))}

            <div className="flex flex-col gap-1.5">
                <span className="lomda-v3-chrome text-sm font-semibold text-foreground">
                    {t("lomda-v3-editor.quiz.explanation")}
                </span>
                <input
                    value={explanation}
                    onChange={(e) => onExplanation(e.target.value)}
                    placeholder={t(
                        "lomda-v3-editor.complete-sentence.explanation-placeholder"
                    )}
                    className={cn("w-full", FIELD)}
                />
            </div>
        </div>
    );
}

export default function CompleteSentenceBlock({
    block,
}: {
    block: CompleteSentenceBlockType;
}) {
    const locale = useLomdaLocale();
    const editable = useBlockEditable();
    const replaceBlockData = useLomdaV3Editor((s) => s.replaceBlockData);
    const dir = useLomdaDir();
    const theme = useLomdaV3Theme();
    const portalContainer = usePortalContainer();
    const { text, answers, explanation } = block.data;

    const [picks, setPicks] = useState<Record<number, string>>({});
    const [checked, setChecked] = useState(false);

    const blanks = useMemo(() => blanksOf(text), [text]);

    // Per-blank shuffled options = that blank's answer + its OWN distractors.
    const optionsPerBlank = useMemo(
        () =>
            blanks.map((b, i) =>
                shuffle(
                    Array.from(
                        new Set(
                            [b.answer, ...(answers[i] ?? [])]
                                .map((w) => w.trim())
                                .filter(Boolean)
                        )
                    )
                )
            ),
        [blanks, answers]
    );

    const patch = (p: Partial<CompleteSentenceBlockType["data"]>) =>
        replaceBlockData(block.id, { ...block.data, ...p });

    if (editable) {
        return (
            <EditMode
                text={text}
                blanks={blanks}
                answers={answers}
                explanation={explanation}
                onText={(html) => patch({ text: html })}
                onAnswers={(a) => patch({ answers: a })}
                onExplanation={(e) => patch({ explanation: e })}
            />
        );
    }

    if (!stripHtml(text)) return null;

    const blankPlaceholder = t(
        "lomda-v3-editor.complete-sentence.blank-choose",
        locale
    );
    const { Tag, text: sentence } = parseSentence(text);
    const segments = toSegments(sentence);
    const allPicked = blanks.every((b) => (picks[b.index] ?? "").length > 0);
    const isRight = (b: { index: number; answer: string }) =>
        normalize(picks[b.index] ?? "") === normalize(b.answer);
    const allCorrect = blanks.length > 0 && blanks.every(isRight);

    return (
        <div className="flex flex-col gap-4">
            {/* Fixed instruction line — not authored per block, just localized.
                Styled as the quiz's auto instruction line (small + muted), so
                the sentence itself reads as the main content. */}
            <span className="lomda-v3-text-small text-[hsl(var(--lomda-muted-text))]">
                {t("lomda-v3-editor.complete-sentence.title", locale)}
            </span>
            <div className="lomda-v3-rich-text">
                <Tag style={{ lineHeight: 2.4 }} className="text-foreground">
                    {segments.map((seg, i) =>
                        seg.kind === "text" ? (
                            <span key={i}>{seg.value}</span>
                        ) : (
                            <Select
                                key={i}
                                dir={dir}
                                value={picks[seg.index] || undefined}
                                disabled={checked && isRight(seg)}
                                onValueChange={(v) => {
                                    setPicks((p) => ({ ...p, [seg.index]: v }));
                                    if (checked) setChecked(false);
                                }}
                            >
                                <SelectTrigger
                                    style={{
                                        font: "inherit",
                                        lineHeight: 1,
                                        // Sized to the longest option/placeholder,
                                        // so picking never reflows the sentence.
                                        width: triggerWidth(
                                            optionsPerBlank[seg.index] ?? [],
                                            blankPlaceholder
                                        ),
                                    }}
                                    className={cn(
                                        // A blank line in the sentence: card fill +
                                        // underline, no box or chevron — reads as
                                        // "_____". h-[1em]/leading-none keeps the
                                        // hit area exactly the text height. Until
                                        // it's answered the placeholder renders in
                                        // muted SMALL text — `!` because the inline
                                        // `font: inherit` would otherwise win.
                                        "mx-1 inline-flex h-[1em] min-h-0 max-w-full items-center justify-center rounded-none border-0 border-b bg-transparent px-2 py-0 align-baseline leading-none text-foreground [&>span]:leading-none [&>svg]:hidden focus:ring-0 focus-visible:ring-0 focus:outline-none data-[placeholder]:text-[hsl(var(--lomda-muted-text))] data-[placeholder]:!text-[length:var(--lomda-size-small)]",
                                        checked
                                            ? isRight(seg)
                                                ? "border-[hsl(var(--lomda-success))]"
                                                : "border-destructive"
                                            : "border-[hsl(var(--lomda-text))]"
                                    )}
                                >
                                    <SelectValue
                                        placeholder={blankPlaceholder}
                                    />
                                </SelectTrigger>
                                {/* The dropdown portals to <body>, outside the
                                    themed page — carry the lomda's tokens and
                                    typography with it so the options match the
                                    sentence, not the platform chrome. In
                                    fullscreen it must portal into the player
                                    instead: <body> isn't painted then. No text
                                    colour here: the dropdown is a popover, so
                                    its own `text-popover-foreground` already
                                    resolves to "Text on cards". */}
                                <SelectContent
                                    container={portalContainer}
                                    style={theme}
                                    className="lomda-v3-text-p"
                                >
                                    {(optionsPerBlank[seg.index] ?? []).map(
                                        (w) => (
                                            <SelectItem key={w} value={w}>
                                                {w}
                                            </SelectItem>
                                        )
                                    )}
                                </SelectContent>
                            </Select>
                        )
                    )}
                </Tag>
            </div>

            {checked && (
                <div
                    className={cn(
                        "flex items-start gap-2 rounded-xl p-3 lomda-v3-text-p animate-in fade-in",
                        allCorrect
                            ? "bg-[hsl(var(--lomda-success))]/10 text-[hsl(var(--lomda-success))]"
                            : "bg-[hsl(var(--lomda-destructive))]/10 text-[hsl(var(--lomda-destructive))]"
                    )}
                >
                    {allCorrect ? (
                        <CheckCircle2 className="mt-0.5 size-5 shrink-0" />
                    ) : (
                        <XCircle className="mt-0.5 size-5 shrink-0" />
                    )}
                    <span>
                        {allCorrect
                            ? explanation ||
                              t(
                                  "lomda-v3-editor.complete-sentence.correct",
                                  locale
                              )
                            : t(
                                  "lomda-v3-editor.complete-sentence.try-again",
                                  locale
                              )}
                    </span>
                </div>
            )}

            {!(checked && allCorrect) && (
                <PreviewButton
                    type="button"
                    disabled={!allPicked}
                    onClick={() => setChecked(true)}
                >
                    {t("lomda-v3-editor.complete-sentence.check", locale)}
                </PreviewButton>
            )}
        </div>
    );
}
